import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Save, X, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { enrollApi } from '@/pages/dashboard/StudentEnrollmentPage';

export const StudentEnrolmentPanel = ({ student, classes, onChanged }) => {
  const [checked, setChecked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [showAllYears, setShowAllYears] = useState(false);

  useEffect(() => { setChecked(new Set((student?.classes || []).map((c) => c.class_id))); setShowAllYears(false); }, [student]);

  const visible = useMemo(() => {
    const list = showAllYears || !student?.year ? classes : classes.filter((c) => c.year === student.year);
    return list.reduce((acc, c) => { (acc[c.subject] = acc[c.subject] || []).push(c); return acc; }, {});
  }, [classes, student, showAllYears]);

  if (!student) {
    return (
      <Card className="h-full"><CardContent className="py-20 text-center text-muted-foreground">
        <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />Select a student on the left to assign subjects.
      </CardContent></Card>
    );
  }

  const current = new Set(student.classes.map((c) => c.class_id));
  const toAdd = [...checked].filter((id) => !current.has(id));
  const toRemove = [...current].filter((id) => !checked.has(id));
  const dirty = toAdd.length || toRemove.length;

  const toggle = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setSaving(true);
    try {
      if (toAdd.length) await enrollApi('/assign', { method: 'POST', body: JSON.stringify({ student_id: student.id, class_ids: toAdd }) });
      for (const cid of toRemove) await enrollApi('/unassign', { method: 'POST', body: JSON.stringify({ student_id: student.id, class_id: cid }) });
      toast.success(`${student.name}: ${toAdd.length} added, ${toRemove.length} removed`);
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="enrol-panel">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{student.name}</CardTitle>
            <p className="text-sm text-muted-foreground">{[student.year && `Year ${student.year}`, student.student_number && `ID ${student.student_number}`].filter(Boolean).join(' · ')}</p>
          </div>
          <Button onClick={save} disabled={!dirty || saving} className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="enrol-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="enrol-current-classes">
          {student.classes.length === 0 && <span className="text-xs text-muted-foreground">Not enrolled in any subject yet</span>}
          {student.classes.map((c) => (
            <Badge key={c.class_id} variant="outline" className="gap-1 pr-1">
              {c.name}{c.teacher ? <span className="text-muted-foreground">· {c.teacher}</span> : null}
              <button onClick={() => toggle(c.class_id)} className="ml-1 rounded hover:bg-red-100 text-red-600" title="Remove" data-testid={`enrol-remove-${c.class_id}`}><X className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium">Subject classes {student.year && !showAllYears ? `for Year ${student.year}` : '(all years)'}</p>
          {student.year && <button className="text-xs text-teal-700 hover:underline" onClick={() => setShowAllYears((v) => !v)} data-testid="enrol-toggle-years">{showAllYears ? `Only Year ${student.year}` : 'Show all years'}</button>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 max-h-[50vh] overflow-auto pr-1">
          {Object.keys(visible).sort().map((subject) => (
            <div key={subject} className="py-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{subject}</p>
              {visible[subject].map((c) => (
                <label key={c.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer" data-testid={`enrol-class-${c.id}`}>
                  <Checkbox checked={checked.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                  <span className="truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground truncate ml-auto">{c.teacher || 'No teacher'} · {c.student_count}</span>
                </label>
              ))}
            </div>
          ))}
          {Object.keys(visible).length === 0 && <p className="text-sm text-muted-foreground col-span-2">No classes for this year yet.</p>}
        </div>
      </CardContent>
    </Card>
  );
};
